import { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import { Target, ShieldCheck, Rocket, HeartHandshake, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
const services = [{
  id: "strategy",
  title: "AI Strategy & Vision",
  description: "Defining your AI transformation vision, objectives, and a clear strategic roadmap.",
  icon: Target,
  duration: "4-6 weeks",
  complexity: "Strategic",
  detailedContent: {
    intro: "AI is reshaping industries, business models, and customer expectations – but for many leaders, the big question remains: What does this mean for us?",
    serviceDescription: "Our AI Business Strategy service helps decision-makers and business leaders who want to move beyond the AI hype and develop a clear, actionable understanding of how artificial intelligence can (and will) impact their organization.",
    explorationPoints: ["What AI is – and what it isn't", "How AI is changing the broader business landscape", "What opportunities and challenges AI may bring to your organization", "What capabilities you need to build today to stay competitive tomorrow"],
    process: "Through executive workshops, strategic assessments, and hands-on collaboration, we help you define your AI ambition, align it with your business goals, and map the path forward.",
    result: "The result? A tailored AI strategy that gives you clarity, confidence, and a roadmap for responsible transformation, both short-term wins and long-term value."
  }
}, {
  id: "governance",
  title: "AI Governance & Compliance",
  description: "Developing robust AI governance frameworks and ensuring alignment with the EU AI Act.",
  icon: ShieldCheck,
  duration: "6-8 weeks",
  complexity: "Governance",
  detailedContent: {
    intro: "As AI becomes integral to business operations, ensuring responsible and compliant AI use is no longer optional – it's essential.",
    serviceDescription: "Our AI Governance & Compliance service helps organizations establish clear frameworks for ethical AI development, deployment, and monitoring while ensuring full alignment with emerging regulations like the EU AI Act.",
    explorationPoints: ["Understanding EU AI Act requirements and risk classifications", "Establishing AI ethics principles and decision-making frameworks", "Implementing accountability structures and documentation practices", "Building monitoring systems for ongoing compliance and safety"],
    process: "We work closely with your leadership, legal, and technical teams to design governance structures that balance innovation with responsibility, creating practical policies that protect your organization while enabling AI growth.",
    result: "You'll gain a comprehensive governance framework that ensures compliant, ethical AI use that gives stakeholders confidence and your organization a competitive advantage through trusted AI practices."
  }
}, {
  id: "implementation",
  title: "AI Implementation Leadership",
  description: "Leading programs and projects to effectively integrate AI solutions.",
  icon: Rocket,
  duration: "3-6 months",
  complexity: "Tactical",
  detailedContent: {
    intro: "Having an AI strategy is one thing – executing it successfully is another. Many organizations struggle to bridge the gap between vision and reality.",
    serviceDescription: "Our AI Implementation Leadership service provides experienced leadership to guide your AI initiatives from concept to deployment, ensuring projects deliver real business value while managing risks and change effectively.",
    explorationPoints: ["Designing pilot projects that demonstrate quick wins", "Building cross-functional teams with the right mix of skills", "Managing stakeholder expectations and organizational change", "Scaling successful pilots into enterprise-wide solutions"],
    process: "We embed with your teams as interim AI leaders or program managers, bringing proven methodologies, technical expertise, and change management skills to drive momentum and overcome implementation challenges.",
    result: "Your AI initiatives move from planning to production with confidence, delivering measurable results while building internal capabilities for sustained AI success."
  }
}, {
  id: "support",
  title: "Ongoing AI Support & Coaching",
  description: "Providing continuous guidance for your AI team and key stakeholders.",
  icon: HeartHandshake,
  duration: "Ongoing",
  complexity: "Dynamic",
  detailedContent: {
    intro: "AI transformation is a journey, not a destination. Even after initial implementation, organizations need ongoing support to navigate evolving technologies, challenges, and opportunities.",
    serviceDescription: "Our Ongoing AI Support & Coaching service provides continuous advisory support to your leadership and teams, helping you adapt to new developments, optimize existing initiatives, and maintain momentum in your AI transformation.",
    explorationPoints: ["Regular strategic check-ins and advisory sessions", "Coaching for AI leaders and team members", "Guidance on emerging AI technologies and trends", "Troubleshooting implementation challenges and roadblocks", "Change management and communication"],
    process: "Through flexible engagement models – from monthly advisory sessions to on-demand support – we serve as your trusted AI partner, providing expert guidance whenever you need it.",
    result: "You'll have a reliable AI advisor by your side, ensuring your organization stays current, overcomes obstacles quickly, and continues to extract maximum value from AI investments."
  }
}];
const Services = () => {
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };
  
  return (
    <>
      <Helmet>
        <title>Our Services | AI-labbet</title>
        <meta name="description" content="Explore AI-labbet's services: AI strategy consulting, governance frameworks, implementation support, and ongoing coaching." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        <PageBreadcrumb currentPage="Services" />
      
      {/* Hero Section */}
      <main>
        <section className="relative py-20 md:py-32 overflow-hidden bg-background">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          
          <div className="relative z-10 container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive AI leadership and business 
guidance tailored to your needs
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 px-6 bg-background">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {services.map(service => {
            const IconComponent = service.icon;
            const isExpanded = expandedService === service.id;
            return <Collapsible key={service.id} open={isExpanded} onOpenChange={() => toggleService(service.id)}>
                  <div id={service.id} className={`
                      group rounded-2xl border bg-card shadow-lg p-6 md:p-8
                      transition-all duration-300
                      hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(0,198,255,0.2)]
                      ${isExpanded ? 'border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 shadow-2xl -translate-y-1' : 'border-border/50 hover:border-primary/30'}
                    `}>
                    {/* Icon & Badges */}
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                        <IconComponent className="w-7 h-7 text-primary" />
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Badge variant="secondary" className="text-xs">
                          {service.duration}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {service.complexity}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-4">
                      {service.title}
                    </h2>
                    
                    {/* Short Description */}
                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                      {service.description}
                    </p>
                    
                    {/* Expandable Content */}
                    <CollapsibleContent>
                      <div className="mt-6 pt-6 border-t border-border/50 animate-fade-in">
                        <div className="space-y-6 text-left">
                          <p className="text-lg text-foreground/90 leading-relaxed">
                            {service.detailedContent.intro}
                          </p>
                          
                          <p className="text-base text-muted-foreground leading-relaxed">
                            {service.detailedContent.serviceDescription}
                          </p>
                          
                          <div>
                            <p className="text-lg font-semibold mb-3 text-foreground">
                              We work with you to explore:
                            </p>
                            <ul className="space-y-2">
                              {service.detailedContent.explorationPoints.map((point, idx) => <li key={idx} className="flex items-start">
                                  <span className="text-primary mr-3 mt-1 text-xl">•</span>
                                  <span className="text-base text-muted-foreground">{point}</span>
                                </li>)}
                            </ul>
                          </div>
                          
                          <p className="text-base text-muted-foreground leading-relaxed">
                            {service.detailedContent.process}
                          </p>
                          
                          <p className="text-base font-semibold text-foreground leading-relaxed">
                            {service.detailedContent.result}
                          </p>
                        </div>
                      </div>
                    </CollapsibleContent>
                    
                    {/* Read More Button */}
                    <CollapsibleTrigger asChild>
                      <Button variant="tertiary" className="w-full md:w-auto mt-6 group">
                        {isExpanded ? 'Show Less' : 'Read More'}
                        <ChevronDown className={`ml-2 h-4 w-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                </Collapsible>;
          })}
          </div>
        </div>
      </section>

      <CallToAction />
      </main>
      <Footer />
    </div>
    </>
  );
};
export default Services;